import { apiClient } from "../axiosInstance";

class DashboardService {
  constructor() {
    this.adminBaseUrl = "/admin";
  }

  /**
   * Get dashboard statistics
   */
  async getStats() {
    try {
      const [
        adminStats,
        requestStats,
        messageStats,
        blogStats,
        productStats,
        notificationStats,
      ] = await Promise.allSettled([
        apiClient.get(`/admins/stats`).catch(() => ({ data: {} })),
        apiClient
          .get(`/requests/admin/requests/stats`)
          .catch(() => ({ data: {} })),
        apiClient
          .get(`/messages/admin/messages/stats`)
          .catch(() => ({ data: {} })),
        apiClient.get(`/blogs/admin/blogs/stats`).catch(() => ({ data: {} })),
        apiClient.get(`/products/stats`).catch(() => ({ data: {} })),
        apiClient.get(`/notifications/stats`).catch(() => ({ data: {} })),
      ]);

      // Extract data - handle different response structures
      const extractData = (response) => {
        if (!response || response.status === "rejected") return {};
        const value = response.value?.data || {};
        // Handle different data structures
        return value?.stats || value?.data || value || {};
      };

      const admins = extractData(adminStats);
      const requests = extractData(requestStats);
      const messages = extractData(messageStats);
      const blogs = extractData(blogStats);
      const products = extractData(productStats);
      const notifications = extractData(notificationStats);

      console.log("Products stats:", products); // Debug log

      return {
        success: true,
        data: {
          admins: {
            total: admins.total || 0,
            active: admins.active || 0,
            inactive: admins.inactive || 0,
            superAdmins: admins.byRole?.super_admin || 0,
            admins: admins.byRole?.admin || 0,
            editors: admins.byRole?.editor || 0,
            recentlyActive: admins.recentlyActive || 0,
          },
          requests: {
            total: requests.total || 0,
            pending: requests.pending || requests.pendingCount || 0,
            urgent: requests.urgent || requests.urgentCount || 0,
            completed: requests.completed || requests.byStatus?.completed || 0,
            cancelled: requests.cancelled || requests.byStatus?.cancelled || 0,
            thisWeek: requests.thisWeek || 0,
            thisMonth: requests.thisMonth || 0,
            completionRate: requests.completionRate || 0,
            averageResponseTime: requests.averageResponseTime || 0,
          },
          messages: {
            total: messages.total || 0,
            unread: messages.unread || 0,
            overdue: messages.overdue || 0,
            resolved: messages.resolved || messages.byStatus?.resolved || 0,
            spam: messages.spam || messages.byStatus?.spam || 0,
            thisWeek: messages.thisWeek || 0,
            thisMonth: messages.thisMonth || 0,
            averageResponseTime: messages.averageResponseTime || 0,
            resolvedRate: messages.resolvedRate || 0,
          },
          blogs: {
            total: blogs.total || 0,
            published: blogs.published || 0,
            drafts: blogs.drafts || 0,
            scheduled: blogs.scheduled || 0,
            archived: blogs.archived || 0,
            featured: blogs.featured || 0,
            totalViews: blogs.totalViews || 0,
            totalLikes: blogs.totalLikes || 0,
            thisWeek: blogs.thisWeek || 0,
            thisMonth: blogs.thisMonth || 0,
          },
          products: {
            total: products.total || 0,
            active: products.active || 0,
            inactive: products.inactive || 0,
            featured: products.featured || 0,
            new: products.new || products.isNew || 0,
            totalViews: products.totalViews || 0,
            byCategory: products.byCategory || { food: 0, non_food: 0 },
          },
          notifications: {
            total: notifications.total || 0,
            unread: notifications.unread || 0,
            expired: notifications.expired || 0,
            thisWeek: notifications.thisWeek || 0,
            thisMonth: notifications.thisMonth || 0,
          },
        },
      };
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      return {
        success: false,
        error: error.message,
        data: this.getEmptyStats(),
      };
    }
  }

  /**
   * Get empty stats structure
   */
  getEmptyStats() {
    return {
      admins: {
        total: 0,
        active: 0,
        inactive: 0,
        superAdmins: 0,
        admins: 0,
        editors: 0,
        recentlyActive: 0,
      },
      requests: {
        total: 0,
        pending: 0,
        urgent: 0,
        completed: 0,
        cancelled: 0,
        thisWeek: 0,
        thisMonth: 0,
        completionRate: 0,
        averageResponseTime: 0,
      },
      messages: {
        total: 0,
        unread: 0,
        overdue: 0,
        resolved: 0,
        spam: 0,
        thisWeek: 0,
        thisMonth: 0,
        averageResponseTime: 0,
        resolvedRate: 0,
      },
      blogs: {
        total: 0,
        published: 0,
        drafts: 0,
        scheduled: 0,
        archived: 0,
        featured: 0,
        totalViews: 0,
        totalLikes: 0,
        thisWeek: 0,
        thisMonth: 0,
      },
      products: {
        total: 0,
        active: 0,
        inactive: 0,
        featured: 0,
        new: 0,
        totalViews: 0,
        byCategory: { food: 0, non_food: 0 },
      },
      notifications: {
        total: 0,
        unread: 0,
        expired: 0,
        thisWeek: 0,
        thisMonth: 0,
      },
    };
  }

  /**
   * Get recent activities across modules
   */
  async getRecentActivity(limit = 10) {
    try {
      const [recentProducts, recentRequests, recentMessages, recentBlogs] =
        await Promise.allSettled([
          apiClient.get("/products", {
            params: { sortBy: "createdAt", sortOrder: "DESC", limit: 5 },
          }),
          apiClient.get("/requests/admin/requests", {
            params: { sortBy: "createdAt", sortOrder: "DESC", limit: 5 },
          }),
          apiClient.get("/messages/admin/messages", {
            params: { sortBy: "createdAt", sortOrder: "DESC", limit: 5 },
          }),
          apiClient.get("/blogs/admin/blogs", {
            params: { sortBy: "createdAt", sortOrder: "DESC", limit: 5 },
          }),
        ]);

      const activities = [];

      // Extract products
      if (recentProducts.status === "fulfilled") {
        const products =
          recentProducts.value.data?.data?.products ||
          recentProducts.value.data?.products ||
          [];
        products.forEach((product) => {
          activities.push({
            type: "product",
            action: product.isNew ? "added" : "updated",
            item: product.name,
            time: product.createdAt,
            id: product.id,
          });
        });
      }

      // Extract requests
      if (recentRequests.status === "fulfilled") {
        const requests =
          recentRequests.value.data?.data?.requests ||
          recentRequests.value.data?.requests ||
          [];
        requests.forEach((request) => {
          activities.push({
            type: "request",
            action: request.status || "created",
            item: `Request #${request.requestNumber}`,
            time: request.createdAt,
            id: request.id,
            status: request.status,
          });
        });
      }

      // Extract messages
      if (recentMessages.status === "fulfilled") {
        const messages =
          recentMessages.value.data?.data?.messages ||
          recentMessages.value.data?.messages ||
          [];
        messages.forEach((message) => {
          activities.push({
            type: "message",
            action: message.status || "received",
            item: `Message from ${message.name || "Customer"}`,
            time: message.createdAt,
            id: message.id,
            status: message.status,
          });
        });
      }

      // Extract blogs
      if (recentBlogs.status === "fulfilled") {
        const blogs =
          recentBlogs.value.data?.data?.blogs ||
          recentBlogs.value.data?.blogs ||
          [];
        blogs.forEach((blog) => {
          activities.push({
            type: "blog",
            action: blog.status || "created",
            item: blog.title,
            time: blog.createdAt,
            id: blog.id,
            status: blog.status,
          });
        });
      }

      // Sort and limit
      const sortedActivities = activities
        .sort((a, b) => new Date(b.time) - new Date(a.time))
        .slice(0, limit);

      return {
        success: true,
        data: sortedActivities,
      };
    } catch (error) {
      console.error("Error fetching recent activity:", error);
      return { success: false, data: [] };
    }
  }

  /**
   * Get current user from localStorage
   */
  async getCurrentUser() {
    try {
      const userStr =
        localStorage.getItem("user") || localStorage.getItem("admin");
      if (!userStr) return null;
      return JSON.parse(userStr);
    } catch (error) {
      console.error("Error getting current user:", error);
      return null;
    }
  }

  /**
   * Get performance metrics
   */
  async getPerformanceMetrics() {
    try {
      const stats = await this.getStats();
      if (!stats.success) throw new Error(stats.error);

      const data = stats.data;
      return {
        success: true,
        data: {
          requestCompletionRate: data.requests.completionRate || 0,
          messageResponseTime: data.messages.averageResponseTime || 0,
          messageResolutionRate: data.messages.resolvedRate || 0,
          productGrowth: 0,
          requestGrowth: 0,
          messageGrowth: 0,
        },
      };
    } catch (error) {
      console.error("Error fetching performance metrics:", error);
      return {
        success: false,
        data: {
          requestCompletionRate: 0,
          messageResponseTime: 0,
          messageResolutionRate: 0,
        },
      };
    }
  }
}

export const dashboardService = new DashboardService();
